export enum MetadataField {
    Title = 'Title',
    RecordType = 'RecordType',
    RecordStatus = 'RecordStatus',
    AnalogCluster = 'AnalogCluster',
    Carrier = 'Carrier',
    AnalogRepresentationStatus = 'AnalogRepresentationStatus',
    RecordRejectionsRejectionDate = 'RecordRejectionsRejectionDate',
    DossierDeletionDate = 'DossierDeletionDate',
    DossierName = 'DossierName',
    DossierDestructionDate = 'DossierDestructionDate',
    PublicationDate = 'PublicationDate',
    SeriesObjectiveChoice = 'SeriesObjectiveChoice',
    TasksTaskId = 'TasksTaskId',
    TasksTaskDueDate = 'TasksTaskDueDate',
    TasksTaskTypeName = 'TasksTaskTypeName',
    LastModifiedDate = 'LastModifiedDate',
    UserLastModifiedDate = 'UserLastModifiedDate',
    SeriesDisplayName = 'SeriesDisplayName',
    ArchiveDate = 'ArchiveDate',
    MediaObjectId = 'MediaObjectId',
    RecordId = 'RecordId',
    ReferenceCodesDossier = 'ReferenceCodesDossier',
    ReferenceCodesDocument = 'ReferenceCodesDocument',
    /** The flat, queryable form. {@link DottedKey.RelationsIsCandidateChildOf} is the dotted one. */
    RelationsIsCandidateChildOf = 'RelationsIsCandidateChildOf',
    OrganisationLongName = 'OrganisationLongName',
    DossierAnalog = 'DossierAnalog',
    DocumentAnalog = 'DocumentAnalog',
    VersioningId = 'VersioningId',
    VersioningStatus = 'VersioningStatus',
    VersioningVersion = 'VersioningVersion',
    SeriesResponsiblesResponsible = 'SeriesResponsiblesResponsible',
    SeriesApprovalDate = 'SeriesApprovalDate',
    DataRecordId = 'DataRecordId',
    ParentRecordId = 'ParentRecordId',
    UploadedBy = 'UploadedBy',
    DavExpiryExpectedDate = 'DavExpiryExpectedDate',
    /** The detail surfaces' `Bestandsformaat` column; `CustomDossierColumns.OriginalExtension` is the same key. */
    OriginalExtension = 'OriginalExtension'
}

export enum DottedKey {
    ArchiveDate = 'Administrative.ArchiveDate',
    UploadedBy = 'Descriptive.UploadedBy',
    OrganisationLongName = 'Administrative.OrganisationLongName',
    RecordType = 'Administrative.RecordType',
    RecordStatus = 'Administrative.RecordStatus',
    RecordId = 'Internal.RecordId',
    FragmentId = 'Internal.FragmentId',
    MergeStrategiesRelations = 'MergeStrategies.Relations',
    MergeStrategiesAnalogRepresentation = 'MergeStrategies.AnalogRepresentation',
    RelationsIsHeldBy = 'Structural.Relations.IsHeldBy',
    RelationsHolds = 'Structural.Relations.Holds',
    RelationsStores = 'Structural.Relations.Stores',
    RelationsIsStoredOn = 'Structural.Relations.IsStoredOn',
    RelationsRequests = 'Structural.Relations.Requests',
    RelationsIsRequestedBy = 'Structural.Relations.IsRequestedBy',
    RelationsIsCandidateChildOf = 'Structural.Relations.IsCandidateChildOf',
    /** The form a facet has to be requested under; {@link MetadataField.ReferenceCodesDossier} is the queryable one. */
    ReferenceCodesDossier = 'Structural.ReferenceCodes.Dossier',
    /** The form a facet has to be requested under; {@link MetadataField.ReferenceCodesDocument} is the queryable one. */
    ReferenceCodesDocument = 'Structural.ReferenceCodes.Document',
    RelationsContainedBy = 'Structural.Relations.ContainedBy',
    ReferenceTitlePackagingType = 'Structural.ReferenceTitles.PackagingType',
    ReferenceTitleTransportType = 'Structural.ReferenceTitles.TransportType',
    LastModifiedDate = 'Administrative.LastModifiedDate',
    PickUpAddressStreet = 'Dynamic.PickUpAddress.Street',
    PickUpAddressHouseNumber = 'Dynamic.PickUpAddress.HouseNumber',
    PickUpAddressMailbox = 'Dynamic.PickUpAddress.Mailbox',
    PickUpAddressPostalCode = 'Dynamic.PickUpAddress.PostalCode',
    PickUpAddressMunicipality = 'Dynamic.PickUpAddress.Municipality',
    PickUpNotes = 'Dynamic.PickUpNotes',
    PickUpContactDetailsName = 'Dynamic.PickUpContactDetails.Name',
    PickUpContactDetailsEmail = 'Dynamic.PickUpContactDetails.Email',
    PickUpContactDetailsPhoneNumber = 'Dynamic.PickUpContactDetails.PhoneNumber',
    DesiredPickUpDate = 'Dynamic.DesiredPickUpDate',
    TransferNotes = 'Dynamic.TransferNotes',
    TasksTaskId = 'Workflow.Tasks.Task.Id',
    TasksTaskTypeName = 'Workflow.Tasks.Task.TypeName',
    TasksTaskDueDate = 'Workflow.Tasks.Task.DueDate',
    LocationReadableId = 'Dynamic.LocationReadableId',
    LocationLegacyId = 'Dynamic.LocationLegacyId',
    LocationType = 'Dynamic.LocationType',
    LocationHeight = 'Dynamic.LocationHeight',
    LocationWidth = 'Dynamic.LocationWidth',
    LocationDepth = 'Dynamic.LocationDepth',
    RequestType = 'Dynamic.RequestType',
    RequestAddressStreet = 'Dynamic.RequestAddress.Street',
    RequestAddressHouseNumber = 'Dynamic.RequestAddress.HouseNumber',
    RequestAddressMailbox = 'Dynamic.RequestAddress.Mailbox',
    RequestAddressPostalCode = 'Dynamic.RequestAddress.PostalCode',
    RequestAddressMunicipality = 'Dynamic.RequestAddress.Municipality',
    RequestEmail = 'Dynamic.RequestEmail',
    RequestPhoneNumber = 'Dynamic.RequestPhoneNumber',
    ConsultTime = 'Dynamic.ConsultTime',
    QuarantineDate = 'Dynamic.QuarantineDate',
    AnalogRepresentation = 'Dynamic.AnalogRepresentation',
    PackagingUnits = 'Dynamic.AnalogRepresentation.PackagingUnits',
    PackagingUnitsPackagingUnit = 'Dynamic.AnalogRepresentation.PackagingUnits.PackagingUnit',
    PackagingUnitsPackagingUnitNumber = 'Dynamic.AnalogRepresentation.PackagingUnits.PackagingUnit.Number',
    PackagingUnitsPackagingUnitId = 'Dynamic.AnalogRepresentation.PackagingUnits.PackagingUnit.Id',
    LegacyPackagingUnitId = 'Dynamic.LegacyPackagingUnitId',
    LegacyLocationRange = 'Dynamic.LegacyLocationRange',
    /**
     * The folder structure a ZIP upload was unpacked from, preserved as metadata. One key per level, and only
     * the document's is ever shown in the overview table — see `path-caption.helper`.
     *
     * `DocumentPathTagsTag` is the same location again, but **pre-segmented**: a `ListField` whose `Tag`
     * children are the folders one per entry, which `getField` flattens to a `string[]`. It is what the submap
     * grouping reads, since a segmented value needs no separator convention guessed out of a joined string.
     * ⚠ The two are round-tripped separately by `edepot-backend` and nothing guarantees they agree — read one
     * of them, never both.
     */
    DossierPath = 'Dynamic.DossierPath',
    DocumentPath = 'Dynamic.DocumentPath',
    DocumentPathTagsTag = 'Dynamic.DocumentPathTags.Tag',
    DossierPublicity = 'Dynamic.DossierPublicity',
    DossierPublicityRegimen = 'Dynamic.DossierPublicity.Regimen',
    DocumentPublicity = 'Dynamic.DocumentPublicity',
    DocumentPublicityRegimen = 'Dynamic.DocumentPublicity.Regimen',
    BulkReferenceCode = 'Dynamic.BulkReferenceCode',
    SipReferenceCode = 'Dynamic.SipReferenceCode',
    DavExpiryExpectedDate = 'Dynamic.DavExpiry.ExpectedDate',
    DepotDestructionStatus = 'Dynamic.DepotDestructionStatus',
    UserLastModifiedDate = 'Administrative.UserLastModifiedDate',
    SeriesRetentionPeriodAdministrative = 'Dynamic.SeriesRetentionPeriodAdministrative',
    SeriesRetentionPeriodLegal = 'Dynamic.SeriesRetentionPeriodLegal',
    SeriesProcess = 'Dynamic.SeriesProcess',
    SeriesServiceIpdc = 'Dynamic.SeriesServiceIpdc',
    Title = 'Descriptive.Title',
    RecordReuse = 'Dynamic.RecordReuse',
    VersioningId = 'Structural.Versioning.Id',
    Permissions = 'RightsManagement.Permissions',
    DossierOpeningDate = 'Dynamic.DossierOpeningDate',
    DossierClosureDate = 'Dynamic.DossierClosureDate'
}
